import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import { useCallback, useMemo, useEffect } from 'react';
import MultiSelectAutocompleteFilter from '../../../../Filters/MultiSelectAutocompleteFilter';
import Dropdown from '../../../../Dropdown';
import Filter from '../../../../Filters/Filter';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';
import {
  FlightSearchTypes, allianceAutoComplete, allianceOptions, onlyAllianceOption,
} from '../../../../../constants';

import Checkbox from '../../../../Checkbox';
import DropdownSelectFilter from '../../../../Filters/DropdownSelectFilter';

const AlliancesLookup = ({
  tripId, filterBgColor,
}) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTripSearch = !tripId;

  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const isEmptyFilter = useMemo(() => !_.find(searchInputs.flightSegments,
    (fs) => fs.alliances && fs.alliances.length > 0), [searchInputs.flightSegments]);

  const handleChange = (newValue) => {
    const payload = {
      tripId,
      searchObj: {
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
      },
    };

    if (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY) {
      payload.searchObj.flightSegments[0] = {
        ...payload.searchObj.flightSegments[0],
        ..._.omit(newValue, 'rowIndex'),
      };
    } else if (searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY
      || searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP) {
      payload.searchObj.flightSegments[newValue.rowIndex] = {
        ...payload.searchObj.flightSegments[newValue.rowIndex],
        ..._.omit(newValue, 'rowIndex'),
      };
    }

    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('AlliancesLookup.handleChange', {
      ...newValue,
    });
  };

  const handleHasDifferentAlliance = useCallback((newValue) => {
    const payload = {
      tripId,
      searchObj: {
        ...newValue,
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
      },
    };
    payload.searchObj.flightSegments.forEach((segment, index) => {
      payload.searchObj.flightSegments[index] = {
        ...payload.searchObj.flightSegments[index],
        alliances: '',
      };
    });
    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('AlliancesLookup.handleHasDifferentAlliance', {
      ...newValue,
    });
  }, [dispatch, isTripSearch, searchInputs.flightSegments, track, tripId]);

  const handleClearFilter = useCallback(() => {
    handleHasDifferentAlliance({
      hasDifferentOutboundInboundAlliance: false,
    });

    track('filterBar.clearFilter');
  }, [handleHasDifferentAlliance, track]);

  useEffect(() => {
    if (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY
      && searchInputs.hasDifferentOutboundInboundAlliance) {
      handleHasDifferentAlliance({
        hasDifferentOutboundInboundAlliance: false,
      });
    }
  }, [searchInputs, handleHasDifferentAlliance]);

  const options = searchInputs && searchInputs.hasDifferentOutboundInboundAlliance
    ? onlyAllianceOption : allianceOptions;

  const allianceInputs = (alliancePreferenceProperty, alliances, flightSegmetRowIndex = 0) => {
    const alliancePreference = searchInputs && searchInputs.flightSegments
    && searchInputs.flightSegments[flightSegmetRowIndex][alliancePreferenceProperty];
    const matchingOption = options.find((o) => o.value === alliancePreference);
    const labels = matchingOption ? matchingOption.label : options[0].label;

    return (
      <div className="flex font-normal">
        <div>
          <DropdownSelectFilter
            onChange={(newValue) => handleChange(
              { [alliancePreferenceProperty]: newValue, rowIndex: flightSegmetRowIndex },
            )}
            label={labels}
            options={options}
            selectedOptionValue={(searchInputs.flightSegments
            && searchInputs.flightSegments[flightSegmetRowIndex]
            && searchInputs.flightSegments[flightSegmetRowIndex][alliancePreferenceProperty])
              ? searchInputs.flightSegments[flightSegmetRowIndex][alliancePreferenceProperty]
              : options[0].value}
            defaultOptionValue={(searchInputs.flightSegments
                && searchInputs.flightSegments[flightSegmetRowIndex]
                && searchInputs.flightSegments[flightSegmetRowIndex][alliancePreferenceProperty])
                || options[0].value}
            menuWidth="w-64"
            filterButtonMaxWidth="max-w-full w-24"
            filterSpacingClass="pl-2 py-2 pr-2"
            variant="outline-left-radius"
            fontSize="font-normal"
            disabled={searchInputs.hasDifferentOutboundInboundAlliance && true}
          />
        </div>
        <MultiSelectAutocompleteFilter
          onChange={(newValue) => handleChange({
            [alliances]: newValue, rowIndex: flightSegmetRowIndex,
          })}
          label="Alliance Preference"
          tooltipPrependText="Alliance Preference"
          options={[{ suggests: [...allianceAutoComplete] }]}
          filterMaxWidthClass="max-w-full w-48"
          filterSpacingClass="px-4 py-2"
          values={(searchInputs && searchInputs.flightSegments
            && searchInputs.flightSegments[flightSegmetRowIndex]
            && searchInputs.flightSegments[flightSegmetRowIndex].alliances)
           || searchInputs.alliances}
          bgClassName={!isTripSearch ? '' : filterBgColor}
          variant="outline-right-radius"
        />
      </div>
    );
  };

  return (
    <Filter
      filterMaxWidthClass="max-w-full"
      label="Alliance Preference"
      menuWidthClass="w-max"
      isFilterEmpty={isEmptyFilter}
      handleClear={handleClearFilter}
      variant={isTripSearch ? undefined : 'outline'}
      bgClassName={!isTripSearch ? '' : filterBgColor}
    >
      <Dropdown.Item withGridLayout={false}>
        {searchInputs.flightSearchType !== FlightSearchTypes.ONE_WAY ? (
          <Checkbox
            checked={!!searchInputs.hasDifferentOutboundInboundAlliance}
            onChange={(event, newValue) => handleHasDifferentAlliance({
              hasDifferentOutboundInboundAlliance: newValue,
            })}
            label={searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP ? 'Different outbound/inbound alliances' : 'Different alliances for all flights'}
          />
        ) : <></>}
        {searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY && allianceInputs('alliancePreference', 'alliances')}
        {!searchInputs.hasDifferentOutboundInboundAlliance
        && searchInputs.flightSearchType !== FlightSearchTypes.ONE_WAY && allianceInputs('alliancePreference', 'alliances')}
        {searchInputs.hasDifferentOutboundInboundAlliance
        && searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP
          ? (
            <>
              <div className="mt-2 font-bold">Outbound:</div>
              {allianceInputs('alliancePreference', 'alliances', 0)}
              <div className="mt-5 font-bold">Inbound:</div>
              {allianceInputs('alliancePreference', 'alliances', 1)}
            </>
          ) : <></>}

        {searchInputs.hasDifferentOutboundInboundAlliance
         && searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY
        && searchInputs.flightSegments.map((flightSegment, currentIndex) => (
          <>
            <div key={`${currentIndex.toString()}`} className="mt-4 font-bold">
              Flight
              <span className="mx-1">
                {currentIndex + 1}
                :
              </span>
            </div>
            {allianceInputs('alliancePreference', 'alliances', currentIndex)}
          </>
        ))}

      </Dropdown.Item>
    </Filter>
  );
};

AlliancesLookup.propTypes = {
  tripId: PropTypes.string,
  filterBgColor: PropTypes.string,
};

AlliancesLookup.defaultProps = {
  tripId: undefined,
  filterBgColor: undefined,
};

export default AlliancesLookup;
